// src/components/ModalMovimiento.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { putMovimiento as modifMovimiento } from "../helpers/rutaMovimientos";
import { getCajas } from "../helpers/rutaCajas";
import { getGastos } from "../helpers/rutaGastos";
import { getCamiones } from "../helpers/rutaCamiones";
import { getChoferes } from "../helpers/rutaChoferes";
import { getPersonas } from "../helpers/rutaPersonas";
import { getEmpresas } from "../helpers/rutaEmpresas";
import { getClientes } from "../helpers/rutaClientes";
import { getImputas } from "../helpers/rutaImputas";

const MEDIOS_VALIDOS = ["EFECTIVO", "TRANSFERENCIA", "CHEQUE", "ICHEQ"];

// Devuelve lista tanto si viene {key:[...]} como si viene [...]
const pickList = (res, key) => {
  if (Array.isArray(res?.[key])) return res[key];
  if (Array.isArray(res)) return res;
  return [];
};

// Limpia "COMERCIALIZACION (2)" => "COMERCIALIZACION"
const cleanImputaLabel = (s) =>
  String(s || "")
    .replace(/\s*\(\s*\d+\s*\)\s*$/, "")
    .trim();

const money = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  });

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const ModalMovimiento = ({ movimiento, show, handleClose }) => {
  const [formValues, setFormValues] = useState({
    fecha: "",
    tipo: "",
    medio: "",
    caja: "",

    // 🔥 NUEVO: desglose A/B
    montoB: "", // NEGRO
    conFactura: false,
    montoA: "", // BLANCO (TOTAL con IVA)
    ivaAlicuota: "21", // select string

    imputa: "",
    gasto: "",
    descripcion: "",
    persona: "",
    camion: "",
    chofer: "",
    empresa: "",
    cliente: "",
    libre: "",
  });

  const [cajas, setCajas] = useState({ data: [], loading: true });
  const [gastos, setGastos] = useState({ data: [], loading: true });
  const [imputas, setImputas] = useState({ data: [], loading: true });
  const [personas, setPersonas] = useState({ data: [], loading: true });
  const [camiones, setCamiones] = useState({ data: [], loading: true });
  const [choferes, setChoferes] = useState({ data: [], loading: true });
  const [empresas, setEmpresas] = useState({ data: [], loading: true });
  const [clientes, setClientes] = useState({ data: [], loading: true });

  const [saving, setSaving] = useState(false);

  // Carga combos
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const [rc, rg, rim, rp, rcm, rch, remp, rcli] = await Promise.all([
          getCajas(),
          getGastos(),
          getImputas(),
          getPersonas(),
          getCamiones(),
          getChoferes(),
          getEmpresas(),
          getClientes(),
        ]);
        if (!mounted) return;

        setCajas({ data: pickList(rc, "cajas"), loading: false });
        setGastos({ data: pickList(rg, "gastos"), loading: false });
        setImputas({ data: pickList(rim, "imputas"), loading: false });
        setPersonas({ data: pickList(rp, "personas"), loading: false });
        setCamiones({ data: pickList(rcm, "camiones"), loading: false });
        setChoferes({ data: pickList(rch, "choferes"), loading: false });
        setEmpresas({ data: pickList(remp, "empresas"), loading: false });
        setClientes({ data: pickList(rcli, "clientes"), loading: false });
      } catch (e) {
        if (!mounted) return;
        setCajas({ data: [], loading: false });
        setGastos({ data: [], loading: false });
        setImputas({ data: [], loading: false });
        setPersonas({ data: [], loading: false });
        setCamiones({ data: [], loading: false });
        setChoferes({ data: [], loading: false });
        setEmpresas({ data: [], loading: false });
        setClientes({ data: [], loading: false });
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Inicializa form cuando llega el movimiento
  useEffect(() => {
    if (!movimiento) return;

    const aDB = toNum(movimiento.montoA);
    const bDB = toNum(movimiento.montoB);

    // compat: si no existen A/B, tomamos monto legacy como B
    const hasAB = (movimiento.montoA != null || movimiento.montoB != null) && (aDB !== 0 || bDB !== 0);
    const b = hasAB ? bDB : toNum(movimiento.monto);

    setFormValues({
      fecha: movimiento.fecha ? new Date(movimiento.fecha).toISOString().slice(0, 16) : "",
      tipo: movimiento.tipo || "",
      medio: movimiento.medio || "",
      caja: movimiento.fondo?._id || movimiento.caja?._id || "",

      // desglose
      montoB: b ? String(b) : "",
      conFactura: aDB > 0,
      montoA: aDB ? String(aDB) : "",
      ivaAlicuota: String(movimiento.ivaAlicuota ?? 21),

      gasto: movimiento.gasto?._id || "",
      imputa: movimiento.imputa?._id || "",
      descripcion: movimiento.descripcion || "",
      persona: movimiento.persona?._id || "",
      camion: movimiento.camion?._id || "",
      chofer: movimiento.chofer?._id || "",
      empresa: movimiento.empresa?._id || "",
      cliente: movimiento.cliente?._id || "",
      libre: movimiento.libre || "",
    });
  }, [movimiento]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormValues((s) => {
      const next = { ...s, [name]: type === "checkbox" ? checked : value };

      // si destilda factura, limpiamos A e IVA
      if (name === "conFactura" && checked === false) {
        next.montoA = "";
        next.ivaAlicuota = "21";
      }

      return next;
    });
  };

  // Fondos segun medio
  const fondosFiltrados = useMemo(() => {
    const medio = (formValues.medio || "").toUpperCase().trim();

    const clasifica = (f) => {
      const tipo = (f.tipo || "").toString().toUpperCase().trim();
      const hasBank = Boolean(f.banco || f.alias || f.cbu || f.numero);
      const isBanco = tipo.includes("BANCO") || (!tipo.includes("CAJA") && hasBank);
      const isCaja = tipo.includes("CAJA") || (!isBanco && !tipo.includes("BANCO"));
      return { ...f, _isCaja: isCaja, _isBanco: isBanco };
    };

    const list = (cajas.data || []).map(clasifica);

    if (medio === "EFECTIVO") {
      const solo = list.filter((f) => f._isCaja);
      return solo.length ? solo : list.filter((f) => !f._isBanco);
    }
    if (medio === "TRANSFERENCIA") return list.filter((f) => f._isBanco);
    return list;
  }, [formValues.medio, cajas.data]);

  const combosLoaded =
    !cajas.loading &&
    !gastos.loading &&
    !imputas.loading &&
    !personas.loading &&
    !camiones.loading &&
    !choferes.loading &&
    !empresas.loading &&
    !clientes.loading;

  // Cálculos en vivo (A total con IVA)
  const calc = useMemo(() => {
    const b = toNum(formValues.montoB);
    const conFac = !!formValues.conFactura;
    const aTotal = conFac ? toNum(formValues.montoA) : 0;
    const alic = conFac ? toNum(formValues.ivaAlicuota) : 0;

    let neto = 0;
    let iva = 0;

    if (conFac && aTotal > 0 && alic > 0) {
      neto = aTotal / (1 + alic / 100);
      iva = aTotal - neto;
    } else if (conFac && aTotal > 0) {
      neto = aTotal;
      iva = 0;
    }

    const total = aTotal + b;

    return {
      montoA: Number(aTotal.toFixed(2)),
      montoB: Number(b.toFixed(2)),
      netoA: Number(neto.toFixed(2)),
      ivaA: Number(iva.toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  }, [formValues.montoA, formValues.montoB, formValues.conFactura, formValues.ivaAlicuota]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const medioUp = (formValues.medio || "").toUpperCase().trim();
    if (!MEDIOS_VALIDOS.includes(medioUp)) {
      alert("Seleccioná un medio válido (Efectivo, Transferencia, Cheque o iCheq).");
      return;
    }

    if ((medioUp === "EFECTIVO" || medioUp === "TRANSFERENCIA") && !formValues.caja) {
      alert("Debes seleccionar un Fondo (Caja/Banco) acorde al medio.");
      return;
    }

    if (calc.montoA < 0 || calc.montoB < 0) {
      alert("Los montos no pueden ser negativos.");
      return;
    }
    if (calc.montoA === 0 && calc.montoB === 0) {
      alert("Debes cargar un monto (Negro y/o Blanco).");
      return;
    }
    if (formValues.conFactura && calc.montoA === 0) {
      alert("Marcaste 'Con factura' pero el Monto A está en 0.");
      return;
    }

    const body = {
      fecha: formValues.fecha || undefined,
      tipo: formValues.tipo,
      medio: medioUp,

      // ✅ nuevo desglose
      montoB: calc.montoB,
      ...(formValues.conFactura
        ? {
            montoA: calc.montoA, // total con IVA
            ivaAlicuota: toNum(formValues.ivaAlicuota),
            aIncluyeIVA: true,
          }
        : { montoA: 0, ivaAlicuota: 0, aIncluyeIVA: true }),

      // total legacy (por si tu ruta todavía usa "monto")
      monto: calc.total,

      caja: formValues.caja || undefined, // compat -> back lo mapea a fondo
      imputa: formValues.imputa || undefined,
      gasto: formValues.gasto || undefined,
      descripcion: formValues.descripcion || "",
      persona: formValues.persona || undefined,
      camion: formValues.camion || undefined,
      chofer: formValues.chofer || undefined,
      empresa: formValues.empresa || undefined,
      cliente: formValues.cliente || undefined,
      libre: formValues.libre || "",
    };

    try {
      setSaving(true);
      await modifMovimiento(movimiento._id, body);
      handleClose();
    } catch (err) {
      alert(err?.response?.data?.message || "Error al guardar el movimiento");
    } finally {
      setSaving(false);
    }
  };

  const isOpen = Boolean(show);

  return isOpen ? (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      size="lg"
      dialogClassName="modal-mov-compact"
    >
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Modificar Movimiento</Modal.Title>
        </Modal.Header>

        <Modal.Body className="modal-body-scroll">
          {!combosLoaded ? (
            <div className="text-muted">Cargando…</div>
          ) : (
            <>
              {/* FILA 1 */}
              <div className="row g-2">
                <div className="col-md-6">
                  <label className="form-label">Fecha</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="fecha"
                    required
                    value={formValues.fecha}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Tipo</label>
                  <select
                    className="form-control"
                    name="tipo"
                    value={formValues.tipo}
                    onChange={handleChange}
                    required
                  >
                    <option value="ingreso">Ingreso</option>
                    <option value="egreso">Egreso</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Total</label>
                  <input className="form-control" value={money(calc.total)} disabled />
                  {formValues.conFactura && (
                    <small className="text-muted">
                      Neto: {money(calc.netoA)} · IVA: {money(calc.ivaA)}
                    </small>
                  )}
                </div>
              </div>

              {/* FILA 2 */}
              <div className="row g-2 mt-2">
                <div className="col-md-4">
                  <label className="form-label">Medio</label>
                  <select
                    className="form-control"
                    name="medio"
                    value={formValues.medio}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- seleccionar --</option>
                    <option value="EFECTIVO">Efectivo</option>
                    <option value="TRANSFERENCIA">Transferencia</option>
                    <option value="CHEQUE">Cheque</option>
                    <option value="ICHEQ">iCheq</option>
                  </select>
                </div>

                <div className="col-md-8">
                  <label className="form-label">Fondo</label>
                  <select
                    className="form-control"
                    name="caja"
                    value={formValues.caja}
                    onChange={handleChange}
                    disabled={!formValues.medio}
                  >
                    <option value="">
                      {formValues.medio ? "Seleccione fondo" : "Seleccione medio primero"}
                    </option>
                    {fondosFiltrados.map((f) => (
                      <option key={f._id} value={f._id}>
                        {f.descripcion}{" "}
                        {f._isBanco ? `(${f.alias || f.banco || f.numero || ""})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* FILA 3: DESGLOSE (alineado prolijo) */}
              <div className="row g-2 mt-2 align-items-start">
                <div className="col-md-3">
                  <label className="form-label">Monto B (Negro)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="montoB"
                    value={formValues.montoB}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                  <div className="form-text text-muted mov-helper">{money(calc.montoB)}</div>
                </div>

                {/* CHECKBOX en la misma fila, texto más chico */}
                <div className="col-md-2">
                  <label className="form-label d-block">&nbsp;</label>
                  <div className="form-check d-flex align-items-center gap-2 mov-checkwrap">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="conFactura"
                      name="conFactura"
                      checked={!!formValues.conFactura}
                      onChange={handleChange}
                    />
                    <label className="form-check-label mov-confactura" htmlFor="conFactura">
                      Con factura
                    </label>
                  </div>
                  <div className="form-text text-muted mov-helper">&nbsp;</div>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Monto A (Blanco - Total)</label>
                  <input
                    type="number"
                    className="form-control"
                    name="montoA"
                    value={formValues.montoA}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    disabled={!formValues.conFactura}
                  />
                  <div className="form-text text-muted mov-helper">{money(calc.montoA)}</div>
                </div>

                <div className="col-md-2">
                  <label className="form-label">IVA</label>
                  <select
                    className="form-control"
                    name="ivaAlicuota"
                    value={formValues.ivaAlicuota}
                    onChange={handleChange}
                    disabled={!formValues.conFactura}
                  >
                    <option value="0">0%</option>
                    <option value="10.5">10.5%</option>
                    <option value="21">21%</option>
                    <option value="27">27%</option>
                  </select>
                  {/* placeholder para que no "baje" */}
                  <div className="form-text text-muted mov-helper">&nbsp;</div>
                </div>

                <div className="col-md-2">
                  <label className="form-label">Total</label>
                  <input
                    className="form-control"
                    value={money(calc.total)}
                    disabled
                    readOnly
                  />
                  <div className="form-text text-muted mov-helper">
                    {formValues.conFactura
                      ? `Neto: ${money(calc.netoA)} · IVA: ${money(calc.ivaA)}`
                      : `A+B = ${money(calc.total)}`}
                  </div>
                </div>
</div>

              {/* FILA 4 */}
              <div className="row g-2 mt-2">
                <div className="col-md-4">
                  <label className="form-label">Empresa</label>
                  <select
                    className="form-control"
                    name="empresa"
                    value={formValues.empresa}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione empresa</option>
                    {empresas.data
                      .filter((e) => e.activo !== false)
                      .map((e) => (
                        <option key={e._id} value={e._id}>
                          {e.empresa || e.razonSocial || `#${e.codemp || ""}`}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Imputa</label>
                  <select
                    className="form-control"
                    name="imputa"
                    value={formValues.imputa}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione imputa</option>
                    {imputas.data.map((i) => (
                      <option key={i._id} value={i._id}>
                        {cleanImputaLabel(i.imputa)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Tipo de Gasto</label>
                  <select
                    className="form-control"
                    name="gasto"
                    value={formValues.gasto}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione gasto</option>
                    {gastos.data.map((g) => (
                      <option key={g._id} value={g._id}>
                        {g.gasto}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* FILA 5 */}
              <div className="row g-2 mt-2">
                <div className="col-md-3">
                  <label className="form-label">Persona</label>
                  <select
                    className="form-control"
                    name="persona"
                    value={formValues.persona}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione persona</option>
                    {personas.data.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.codper ? `${p.codper} - ${p.nombre}` : p.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Vehículo</label>
                  <select
                    className="form-control"
                    name="camion"
                    value={formValues.camion}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione camión</option>
                    {camiones.data.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.camion}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Chofer</label>
                  <select
                    className="form-control"
                    name="chofer"
                    value={formValues.chofer}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione chofer</option>
                    {choferes.data.map((ch) => (
                      <option key={ch._id} value={ch._id}>
                        {ch.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Cliente</label>
                  <select
                    className="form-control"
                    name="cliente"
                    value={formValues.cliente}
                    onChange={handleChange}
                  >
                    <option value="">Seleccione cliente</option>
                    {clientes.data.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.razonsocial || c.nombreFantasia || `#${c.codcli || ""}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* FILA 6 */}
              <div className="mt-2">
                <label className="form-label">Descripción</label>
                <input
                  className="form-control"
                  name="descripcion"
                  value={formValues.descripcion}
                  onChange={handleChange}
                />
              </div>

              <div className="mt-2">
                <label className="form-label">Campo Libre</label>
                <input
                  className="form-control"
                  name="libre"
                  value={formValues.libre}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="dark" disabled={saving}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  ) : null;
};

export default ModalMovimiento;
